import React, { useCallback, useMemo, memo } from 'react';
import { format, set, getHours, getMinutes, getDay, addMinutes } from 'date-fns';
import { BusinessClosedDate } from '@/types';
import { Holiday } from '@/lib/scheduling/holidays-simple';
import HourCell from './HourCell';
import { INTERVAL_MINUTES, ROW_HEIGHT_PX, INTERVALS_PER_HOUR } from './constants';

/**
 * DayRow - Renders visual-only HourCells for a single row in the calendar
 *
 * OPTIMIZED: This component no longer passes interaction handlers to HourCell.
 * All interactions (drop, click, hover) are handled by GridDropOverlay.
 */

interface DayRowProps {
  dayDate: Date;
  dayIndex: number;
  dayOfMonth: number;
  dayKey: string;
  rowIndex: number;
  gridRowIndex: number;
  isToday: boolean;
  isWeekendDay: boolean;
  holiday: Holiday | null;
  isCurrentlyEditing?: boolean;
  businessId?: string | null;
  totalDisplayHours: number;
  effectiveDisplayStartTime: Date;
  effectiveDisplayEndTime: Date;
  hourCellWidthPx: number;
  hideDayColumn?: boolean;
  isEditingMode?: boolean;
  show24Hours?: boolean;
  // Opening hours props
  openingHoursByDayOfWeek: Map<number, any>;
  // Custom closed date (if this day is marked as closed)
  customClosedDate?: BusinessClosedDate | null;
  // Row state
  isFirstRowInDay?: boolean;
  isLastRowInDay?: boolean;
  // Template creation state (for visual cursor change)
  isTemplateCreationInProgress?: boolean;
}

const DayRow: React.FC<DayRowProps> = ({
  dayDate,
  dayIndex,
  dayOfMonth,
  dayKey,
  rowIndex,
  gridRowIndex,
  isToday,
  isWeekendDay,
  holiday,
  isCurrentlyEditing = false,
  businessId,
  totalDisplayHours,
  effectiveDisplayStartTime,
  effectiveDisplayEndTime,
  hourCellWidthPx,
  hideDayColumn = false,
  isEditingMode = false,
  show24Hours = false,
  openingHoursByDayOfWeek,
  customClosedDate,
  isFirstRowInDay = false,
  isLastRowInDay = false,
  isTemplateCreationInProgress = false,
}) => {

  // Check if day has a custom closed date (highest precedence)
  const isCustomClosed = customClosedDate !== undefined && customClosedDate !== null;

  // **PERFORMANCE OPTIMIZATION**: Memoize opening hours calculations
  const openingHoursData = useMemo(() => {
    const dayOfWeekNum = getDay(dayDate); // 0=Sunday, 1=Monday, etc.
    const openingHoursForDay = openingHoursByDayOfWeek.get(dayOfWeekNum);
    const hasOpeningHoursData = openingHoursByDayOfWeek.size > 0;

    // Use raw opening hours format: check is_open and is_closed_all_day flags
    const isWeeklyScheduleClosed = hasOpeningHoursData && (!openingHoursForDay?.is_open || openingHoursForDay?.is_closed_all_day === true);

    // Day is closed if either custom closed OR weekly schedule closed
    const isDayClosedAllDay = isCustomClosed || isWeeklyScheduleClosed;

    return {
      dayOfWeekNum,
      openingHoursForDay,
      hasOpeningHoursData,
      isDayClosedAllDay
    };
  }, [dayDate, openingHoursByDayOfWeek, isCustomClosed]);

  const { openingHoursForDay, hasOpeningHoursData, isDayClosedAllDay } = openingHoursData;

  
  // Helper function to check if a time slot is within business hours
  const isTimeSlotWithinBusinessHours = useCallback((cellTime: Date): boolean => {
    if (!businessId || !hasOpeningHoursData || !openingHoursForDay) {
      return true; // Default to business hours when no opening hours data (fallback behavior)
    }

    // If the business is closed all day, no slots are within business hours
    if (!openingHoursForDay.is_open || openingHoursForDay.is_closed_all_day) {
      return false;
    }

    // If business is 24 hours, all slots are within business hours
    if (openingHoursForDay.is_24_hours) {
      return true;
    }
    
    // Check if time is within opening and closing times
    if (!openingHoursForDay.opening_time || !openingHoursForDay.closing_time) {
      return false; // Default to NOT business hours when no specific hours
    }

    const cellTimeString = format(cellTime, 'HH:mm:ss');

    // Parse the closing time and subtract 15 minutes for background logic
    const [closeHour, closeMinute] = openingHoursForDay.closing_time.split(':').map(Number);
    let adjustedCloseHour = closeHour;
    let adjustedCloseMinute = closeMinute - 15;
    
    // Handle minute underflow (e.g., 18:00 - 15min = 17:45)
    if (adjustedCloseMinute < 0) {
      adjustedCloseHour -= 1;
      adjustedCloseMinute = 45;
    }
    
    const adjustedCloseTime = `${adjustedCloseHour.toString().padStart(2, '0')}:${adjustedCloseMinute.toString().padStart(2, '0')}:00`;
    // Use >= for opening time to include the exact opening hour
    const isWithinHours = cellTimeString >= openingHoursForDay.opening_time && cellTimeString <= adjustedCloseTime;
    
    return isWithinHours;
  }, [businessId, hasOpeningHoursData, openingHoursForDay]);
  
  // Helper function to check if business is closed at a specific time
  const isBusinessClosedAtTime = useCallback((cellTime: Date): boolean => {
    if (!businessId || !hasOpeningHoursData || !openingHoursForDay) {
      return false; // Default to open if no data
    }
    
    // Return true if business is closed all day or not open
    return !openingHoursForDay.is_open || openingHoursForDay.is_closed_all_day === true;
  }, [businessId, hasOpeningHoursData, openingHoursForDay]);

  // Calculate total intervals for this row
  const safeTotalDisplayHours = isNaN(totalDisplayHours) || totalDisplayHours <= 0 ? 12 : totalDisplayHours;
  const totalIntervals = Math.ceil(safeTotalDisplayHours * INTERVALS_PER_HOUR);

  // Generate HourCells for this specific row
  // PERFORMANCE: Use for loop instead of Array.from to reduce GC pressure
  const hourCells = useMemo(() => {
    const cells: React.ReactElement[] = [];
    for (let intervalIndex = 0; intervalIndex < totalIntervals; intervalIndex++) {
      const intervalMinutes = intervalIndex * INTERVAL_MINUTES;

      const cellTime = addMinutes(
        set(dayDate, {
          hours: effectiveDisplayStartTime.getHours(),
          minutes: effectiveDisplayStartTime.getMinutes(),
          seconds: 0,
          milliseconds: 0
        }),
        intervalMinutes
      );

      const isHourMarker = getMinutes(cellTime) === 0;

      // Calculate opening hours validation for this time slot
      const isWithinBusinessHours = isTimeSlotWithinBusinessHours(cellTime);
      const isBusinessClosed = isBusinessClosedAtTime(cellTime);

      // Calculate grid position for this HourCell
      const gridColumnStart = hideDayColumn ? intervalIndex + 1 : intervalIndex + 2;

      cells.push(
        <HourCell
          key={`hour-${dayKey}-${rowIndex}-${intervalIndex}`}
          dayDate={dayDate}
          cellTime={cellTime}
          businessId={businessId}
          isHourMarker={isHourMarker}
          isToday={isToday}
          isWeekendDay={isWeekendDay}
          isClosedAllDay={isDayClosedAllDay}
          isWithinBusinessHours={isWithinBusinessHours}
          isBusinessClosed={isBusinessClosed}
          openingHoursForDay={openingHoursForDay}
          dayKey={dayKey}
          intervalIndex={intervalIndex}
          dayIndex={dayIndex}
          totalDisplayHours={safeTotalDisplayHours}
          rowHeightPx={ROW_HEIGHT_PX}
          hourCellWidthPx={hourCellWidthPx}
          hideDayColumn={hideDayColumn}
          isEditingMode={isEditingMode}
          show24Hours={show24Hours}
          rowIndex={rowIndex}
          isFirstRowInDay={isFirstRowInDay}
          isLastRowInDay={isLastRowInDay}
          gridColumn={gridColumnStart}
          gridRow={gridRowIndex}
          isTemplateCreationInProgress={isTemplateCreationInProgress}
        />
      );
    }
    return cells;
  }, [
    totalIntervals, effectiveDisplayStartTime, dayKey, rowIndex, isTimeSlotWithinBusinessHours,
    isBusinessClosedAtTime, hideDayColumn, dayIndex, businessId, isToday, isWeekendDay, isDayClosedAllDay, safeTotalDisplayHours,
    hourCellWidthPx, isEditingMode, show24Hours, isFirstRowInDay, isLastRowInDay, gridRowIndex, isTemplateCreationInProgress,
    dayDate, openingHoursForDay
  ]);

  return (
    <>
      {hourCells}
    </>
  );
};

/**
 * Custom comparison function for DayRow memoization
 * Prevents unnecessary re-renders by comparing props efficiently
 *
 * OPTIMIZED: This component no longer has interaction handlers.
 * All interactions are handled by GridDropOverlay.
 */
const arePropsEqual = (prevProps: DayRowProps, nextProps: DayRowProps): boolean => {
  // Quick primitive comparisons first (most likely to change)
  if (
    prevProps.dayIndex !== nextProps.dayIndex ||
    prevProps.rowIndex !== nextProps.rowIndex ||
    prevProps.gridRowIndex !== nextProps.gridRowIndex ||
    prevProps.dayOfMonth !== nextProps.dayOfMonth ||
    prevProps.dayKey !== nextProps.dayKey ||
    prevProps.isToday !== nextProps.isToday ||
    prevProps.isWeekendDay !== nextProps.isWeekendDay ||
    prevProps.isCurrentlyEditing !== nextProps.isCurrentlyEditing ||
    prevProps.businessId !== nextProps.businessId ||
    prevProps.totalDisplayHours !== nextProps.totalDisplayHours ||
    prevProps.hourCellWidthPx !== nextProps.hourCellWidthPx ||
    prevProps.hideDayColumn !== nextProps.hideDayColumn ||
    prevProps.isEditingMode !== nextProps.isEditingMode ||
    prevProps.show24Hours !== nextProps.show24Hours ||
    prevProps.isFirstRowInDay !== nextProps.isFirstRowInDay ||
    prevProps.isLastRowInDay !== nextProps.isLastRowInDay ||
    prevProps.isTemplateCreationInProgress !== nextProps.isTemplateCreationInProgress
  ) {
    return false;
  }

  // Date comparisons (compare timestamps)
  if (prevProps.dayDate.getTime() !== nextProps.dayDate.getTime()) {
    return false;
  }
  if (prevProps.effectiveDisplayStartTime.getTime() !== nextProps.effectiveDisplayStartTime.getTime()) {
    return false;
  }
  if (prevProps.effectiveDisplayEndTime.getTime() !== nextProps.effectiveDisplayEndTime.getTime()) {
    return false;
  }

  // Reference comparisons for objects (assume stable references from parent)
  if (prevProps.holiday !== nextProps.holiday) {
    return false;
  }
  if (prevProps.customClosedDate !== nextProps.customClosedDate) {
    return false;
  }
  if (prevProps.openingHoursByDayOfWeek !== nextProps.openingHoursByDayOfWeek) {
    return false;
  }

  return true;
};

export default memo(DayRow, arePropsEqual);
